import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/apiClient";
import { queryClient } from "../lib/queryClient";

export interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  category: string;
  image_url: string | null;
  available: boolean;
  created_on: string;
  restaurant_id?: string;
  menu_number?: string | null;
}

export function useMenuItems(
  availableOnly: boolean = true,
  restaurantId?: string
) {
  return useQuery({
    queryKey: ["menu", { availableOnly, restaurantId }],
    queryFn: () => {
      const params: Record<string, string | boolean> = {
        available_only: availableOnly,
      };
      if (restaurantId) {
        params.restaurant_id = restaurantId;
      }
      return apiClient<MenuItem[]>("/menu", {
        params,
      });
    },
  });
}

export function useMenuItem(id: string) {
  return useQuery({
    queryKey: ["menu", id],
    queryFn: () => apiClient<MenuItem>(`/menu/${id}`),
    enabled: !!id,
  });
}

export function useCreateMenuItem() {
  return useMutation({
    mutationFn: (menuItem: Partial<MenuItem>) =>
      apiClient<MenuItem>("/menu", {
        method: "POST",
        body: JSON.stringify(menuItem),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menu"] });
    },
  });
}

export function useUpdateMenuItem() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MenuItem> }) =>
      apiClient<MenuItem>(`/menu/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["menu", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["menu"] });
    },
  });
}
